type ConfirmFn = (opts: {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
}) => Promise<boolean>;

export interface MissedFeeResolution {
  abort: boolean;
  fee: number | null;
  /** Set when nonblank input failed numeric validation; the caller must surface it. */
  error?: string;
}

/**
 * Resolves the missed-appointment fee for a no-show/attendance payload.
 *
 * Per owner decision: a checked "charge missed fee" with a blank fee MAY
 * record $0, but never silently — the user must confirm an explicit
 * "$0 fee" prompt first. Unchecked always sends null (no fee charged).
 * Nonblank input that does not parse as a number is rejected with a
 * validation error — it is never coerced to $0 or sent to the API.
 */
export async function resolveMissedAppointmentFee(
  chargeMissedFee: boolean,
  rawFee: string | null | undefined,
  confirm: ConfirmFn,
): Promise<MissedFeeResolution> {
  if (!chargeMissedFee) return { abort: false, fee: null };
  const text = (rawFee ?? '').trim();
  if (text.length === 0) {
    const ok = await confirm({
      title: 'Charge $0 missed-appointment fee?',
      message: 'The missed-appointment fee is blank, so this will record a $0 fee. Continue?',
      confirmText: 'Confirm',
    });
    return ok ? { abort: false, fee: 0 } : { abort: true, fee: null };
  }
  const parsed = Number(text);
  if (!Number.isFinite(parsed)) {
    return { abort: true, fee: null, error: 'Enter a valid fee amount, or leave the fee blank.' };
  }
  return { abort: false, fee: parsed };
}
